import {
    getPartnerAppointments,
    getPartnerToken,
} from '@/lib/api/partner-portal/partner-portal-api'
import {
    PartnerAppointment,
    PartnerAppointmentFilters,
} from '@/lib/api/partner-portal/partner-portal.types'
import { endOfYear, format, startOfMonth, startOfWeek, startOfYear, subDays, subYears } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClear, MdSearch } from 'react-icons/md'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_LABELS: Record<string, string> = {
    CONSULTATION: 'Consulta',
    TELECONSULTATION: 'Teleconsulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
}

const TYPE_BADGE_CLASSES: Record<string, string> = {
    CONSULTATION: 'badge-info',
    TELECONSULTATION: 'badge-accent',
    EXAM: 'badge-secondary',
    PROCEDURE: 'badge-success',
}

const STATUS_LABELS: Record<string, string> = {
    SCHEDULED: 'Agendado',
    WAITING: 'Aguardando',
    IN_PROGRESS: 'Em andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    NO_SHOW: 'Faltou',
    REQUESTED: 'Solicitado',
    POSTPONED: 'Adiado',
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
    SCHEDULED: 'badge-warning',
    WAITING: 'badge-info',
    IN_PROGRESS: 'badge-secondary',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
    NO_SHOW: 'badge-error',
    REQUESTED: 'badge-warning',
    POSTPONED: 'badge-ghost',
}

type DatePreset = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'this_year' | 'last_year' | 'custom'

function getPresetDates(preset: Exclude<DatePreset, 'custom'>): { startDate: string; endDate: string } {
    const today = new Date()
    const formatDate = (d: Date) => format(d, 'yyyy-MM-dd')

    switch (preset) {
        case 'today':
            return { startDate: formatDate(today), endDate: formatDate(today) }
        case 'yesterday': {
            const yesterday = subDays(today, 1)
            return { startDate: formatDate(yesterday), endDate: formatDate(yesterday) }
        }
        case 'this_week': {
            const weekStart = startOfWeek(today, { locale: ptBR })
            return { startDate: formatDate(weekStart), endDate: formatDate(today) }
        }
        case 'this_month': {
            const monthStart = startOfMonth(today)
            return { startDate: formatDate(monthStart), endDate: formatDate(today) }
        }
        case 'this_year': {
            const yearStart = startOfYear(today)
            return { startDate: formatDate(yearStart), endDate: formatDate(today) }
        }
        case 'last_year': {
            const lastYear = subYears(today, 1)
            const lastYearStart = startOfYear(lastYear)
            const lastYearEnd = endOfYear(lastYear)
            return { startDate: formatDate(lastYearStart), endDate: formatDate(lastYearEnd) }
        }
    }
}

export default function PartnerAppointmentsPage() {
    const navigate = useNavigate()
    const [appointments, setAppointments] = useState<PartnerAppointment[]>([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useUrlPage()
    const [totalPages, setTotalPages] = useState(0)
    const [activePreset, setActivePreset] = useState<DatePreset | null>(null)
    const [showCustomDates, setShowCustomDates] = useState(false)

    const [search, setSearch] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [type, setType] = useState('')
    const [status, setStatus] = useState('')

    const filtersRef = useRef({ search: '', startDate: '', endDate: '', type: '', status: '' })

    useEffect(() => {
        if (!getPartnerToken()) {
            navigate('/partner', { replace: true })
            return
        }
        fetchAppointments(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchAppointments = useCallback(
        async (
            targetPage: number,
            append = false,
            overrides?: Partial<typeof filtersRef.current>
        ) => {
            setLoading(true)

            const current = { ...filtersRef.current, ...overrides }

            const filters: PartnerAppointmentFilters = {
                page: targetPage,
                limit: 20,
            }

            if (current.search.trim()) filters.search = current.search.trim()
            if (current.startDate) filters.startDate = current.startDate
            if (current.endDate) filters.endDate = current.endDate
            if (current.type) filters.type = current.type
            if (current.status) filters.status = current.status

            const response = await getPartnerAppointments(filters)

            if (response.success && response.result) {
                if (append) {
                    setAppointments(prev => [...prev, ...response.result!.data])
                } else {
                    setAppointments(response.result.data)
                }
                setTotal(response.result.total)
                setPage(response.result.page)
                setTotalPages(response.result.totalPages)
            } else {
                toast.error(response.error || 'Erro ao buscar agendamentos.')
            }

            setLoading(false)
        },
        []
    )

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        filtersRef.current.search = search
        fetchAppointments(1)
    }

    const handlePreset = (preset: DatePreset) => {
        if (preset === 'custom') {
            setActivePreset('custom')
            setShowCustomDates(true)
            setStartDate('')
            setEndDate('')
            filtersRef.current.startDate = ''
            filtersRef.current.endDate = ''
            return
        }

        const dates = getPresetDates(preset)
        setStartDate(dates.startDate)
        setEndDate(dates.endDate)
        setActivePreset(preset)
        setShowCustomDates(false)
        filtersRef.current.startDate = dates.startDate
        filtersRef.current.endDate = dates.endDate
        fetchAppointments(1, false, { startDate: dates.startDate, endDate: dates.endDate })
    }

    const handleCustomDateFilter = () => {
        filtersRef.current.startDate = startDate
        filtersRef.current.endDate = endDate
        fetchAppointments(1)
    }

    const handleTypeChange = (value: string) => {
        setType(value)
        filtersRef.current.type = value
        fetchAppointments(1, false, { type: value })
    }

    const handleStatusChange = (value: string) => {
        setStatus(value)
        filtersRef.current.status = value
        fetchAppointments(1, false, { status: value })
    }

    const handleClearFilters = () => {
        setSearch('')
        setStartDate('')
        setEndDate('')
        setType('')
        setStatus('')
        setActivePreset(null)
        setShowCustomDates(false)
        filtersRef.current = { search: '', startDate: '', endDate: '', type: '', status: '' }
        fetchAppointments(1, false, { search: '', startDate: '', endDate: '', type: '', status: '' })
    }

    const handlePageChange = (nextPage: number) => {
        fetchAppointments(nextPage)
    }

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '-'
        try {
            return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
        } catch {
            return '-'
        }
    }

    const presets: { key: DatePreset; label: string }[] = [
        { key: 'today', label: 'Hoje' },
        { key: 'yesterday', label: 'Ontem' },
        { key: 'this_week', label: 'Esta semana' },
        { key: 'this_month', label: 'Este mês' },
        { key: 'this_year', label: 'Este ano' },
        { key: 'last_year', label: 'Ano passado' },
        { key: 'custom', label: 'Personalizado' },
    ]

    const hasActiveFilters = search || startDate || endDate || type || status

    return (
        <div className="space-y-4 !max-w-[1200px] !mx-auto">
            {/* Filtros compactos */}
            <div className="card bg-base-100 border border-secondary/20">
                <div className="card-body py-4 px-4 lg:px-6 space-y-3">
                    {/* Linha 1: CPF + Tipo + Status + Limpar */}
                    <div className="flex flex-wrap items-end gap-3">
                        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-[200px] max-w-sm">
                            <div className="input !border-secondary input-bordered input-sm flex items-center gap-2 w-full">
                                <MdSearch className="text-secondary text-lg" />
                                <input
                                    type="text"
                                    placeholder="Paciente, CPF, procedimento..."
                                    className="grow bg-transparent outline-none text-sm"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                        </form>

                        <select
                            className="select select-bordered select-sm w-auto min-w-[130px]"
                            value={type}
                            onChange={e => handleTypeChange(e.target.value)}
                        >
                            <option value="">Todos os tipos</option>
                            <option value="CONSULTATION">Consulta</option>
                            <option value="TELECONSULTATION">Teleconsulta</option>
                            <option value="EXAM">Exame</option>
                            <option value="PROCEDURE">Procedimento</option>
                        </select>

                        <select
                            className="select select-bordered select-sm w-auto min-w-[140px]"
                            value={status}
                            onChange={e => handleStatusChange(e.target.value)}
                        >
                            <option value="">Todos os status</option>
                            <option value="SCHEDULED">Agendado</option>
                            <option value="WAITING">Aguardando</option>
                            <option value="IN_PROGRESS">Em andamento</option>
                            <option value="COMPLETED">Concluído</option>
                            <option value="CANCELLED">Cancelado</option>
                            <option value="NO_SHOW">Faltou</option>
                            <option value="REQUESTED">Solicitado</option>
                        </select>

                        {hasActiveFilters && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={handleClearFilters}
                            >
                                <MdClear className="text-lg" />
                                Limpar
                            </button>
                        )}
                    </div>

                    {/* Linha 2: Presets de data */}
                    <div className="flex flex-wrap gap-2">
                        {presets.map(preset => (
                            <button
                                key={preset.key}
                                type="button"
                                className={`btn btn-xs ${activePreset === preset.key
                                    ? 'btn-secondary'
                                    : 'btn-outline'
                                    }`}
                                onClick={() => handlePreset(preset.key)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {/* Linha 3: Date range personalizado (condicional) */}
                    {showCustomDates && (
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="form-control">
                                <label className="label py-0">
                                    <span className="label-text text-xs">De</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm w-auto"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label py-0">
                                    <span className="label-text text-xs">Até</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm w-auto"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-accent"
                                disabled={loading || (!startDate && !endDate)}
                                onClick={handleCustomDateFilter}
                            >
                                {loading ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Filtrar'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Resultados */}
            <div className="card bg-base-100  border border-secondary/20">
                <div className="card-body">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="card-title text-secondary">Agendamentos </h2>
                        <span className="text-sm text-base-content/60">
                            {total} resultado{total !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {loading && appointments.length === 0 ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-lg" />
                        </div>
                    ) : appointments.length === 0 ? (
                        <div className="text-center py-12 text-base-content/50">
                            <p className="text-lg">
                                Nenhum agendamento encontrado para os filtros
                                selecionados.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Tabela Desktop */}
                            <div className="overflow-x-auto hidden md:block">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th className='text-secondary font-normal'>Data/Hora</th>
                                            <th className='text-secondary font-normal'>Serviço</th>
                                            <th className='text-secondary font-normal'>Paciente</th>
                                            <th className='text-secondary font-normal'>CPF</th>
                                            <th className='text-secondary font-normal'>Tipo</th>
                                            <th className='text-secondary font-normal'>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appointments.map((apt, index) => (
                                            <tr
                                                key={`${apt.type}-${apt.id}-${index}`}
                                                className="cursor-pointer hover:bg-base-200 transition-colors"
                                                onClick={() => navigate(`/partner/appointments/${apt.type}/${apt.id}`)}
                                            >
                                                <td className="whitespace-nowrap">
                                                    {formatDateTime(apt.scheduledAt)}
                                                </td>
                                                <td>{apt.title}</td>
                                                <td>{apt.clientName}</td>
                                                <td className="font-mono text-sm">
                                                    {apt.clientCpf || '-'}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge badge-sm ${TYPE_BADGE_CLASSES[apt.type] || 'badge-ghost'
                                                            }`}
                                                    >
                                                        {TYPE_LABELS[apt.type] || apt.type}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge badge-sm ${STATUS_BADGE_CLASSES[apt.status] || 'badge-ghost'
                                                            }`}
                                                    >
                                                        {STATUS_LABELS[apt.status] || apt.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Cards Mobile */}
                            <div className="space-y-3 md:hidden">
                                {appointments.map((apt, index) => (
                                    <div
                                        key={`${apt.type}-${apt.id}-${index}`}
                                        className="border border-base-300 rounded-lg p-4 space-y-2 cursor-pointer hover:bg-base-200 transition-colors"
                                        onClick={() => navigate(`/partner/appointments/${apt.type}/${apt.id}`)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span
                                                className={`badge badge-sm ${TYPE_BADGE_CLASSES[apt.type] || 'badge-ghost'
                                                    }`}
                                            >
                                                {TYPE_LABELS[apt.type] || apt.type}
                                            </span>
                                            <span
                                                className={`badge badge-sm ${STATUS_BADGE_CLASSES[apt.status] || 'badge-ghost'
                                                    }`}
                                            >
                                                {STATUS_LABELS[apt.status] || apt.status}
                                            </span>
                                        </div>
                                        <div className="text-sm">
                                            <p className="font-bold">{apt.title}</p>
                                            <p className="font-medium">{apt.clientName}</p>
                                            {apt.clientCpf && (
                                                <p className="text-base-content/60 font-mono">
                                                    {apt.clientCpf}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-sm text-base-content/60">
                                            <p>{formatDateTime(apt.scheduledAt)}</p>
                                            {apt.professionalName && (
                                                <p>Prof.: {apt.professionalName}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <PaginationControls
                                    page={page}
                                    limit={20}
                                    total={total}
                                    loading={loading}
                                    onPageChange={handlePageChange}
                                    label="agendamento"
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
